import React, { createContext, useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from './AuthContext';
import { whoami, WhoAmI, ApiError } from '../api';

type Status =
  | { kind: 'checking' }
  | { kind: 'ok'; me: WhoAmI }
  | { kind: 'denied'; me: WhoAmI }
  | { kind: 'error'; message: string };

type Props = { children: React.ReactNode };

// Stash the whoami we already fetched so children don't have to re-fetch
// it to ask role questions. Null when AdminGate hasn't successfully
// resolved an authorized user yet — children rendered inside AdminGate's
// `ok` branch can rely on the value being present.
const WhoAmIContext = createContext<WhoAmI | null>(null);

export const useWhoAmI = (): WhoAmI => {
  const me = useContext(WhoAmIContext);
  if (!me) {
    // Programming error — components inside AdminGate's authorized
    // subtree should always see a non-null value.
    throw new Error('useWhoAmI called outside the authorized subtree');
  }
  return me;
};

const centerSx = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 2,
  px: 2,
  backgroundColor: '#f7f7f7',
} as const;

const AdminGate = ({ children }: Props) => {
  const { idToken, signOut, user } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: 'checking' });

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setStatus({ kind: 'checking' });
    whoami(idToken)
      .then((me) => {
        if (cancelled) return;
        if (me.roles.includes('admin')) {
          setStatus({ kind: 'ok', me });
        } else {
          setStatus({ kind: 'denied', me });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // Token was rejected — Firebase usually refreshes before this happens,
          // so this means the email isn't in any user entry at all.
          setStatus({
            kind: 'denied',
            me: { email: user?.email || '(unknown)', roles: [] },
          });
        } else {
          setStatus({ kind: 'error', message: err?.message || 'unknown error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, user]);

  if (status.kind === 'checking') {
    return (
      <Box sx={centerSx}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (status.kind === 'denied') {
    return (
      <Box sx={centerSx}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
          Not authorized
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 380 }}>
          Talk to the admin.
        </Typography>
        <Button variant="outlined" onClick={signOut} sx={{ textTransform: 'none' }}>
          Sign out
        </Button>
      </Box>
    );
  }
  if (status.kind === 'error') {
    return (
      <Box sx={centerSx}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
          Backend unreachable
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 380 }}>
          {status.message}
        </Typography>
        <Button variant="outlined" onClick={signOut} sx={{ textTransform: 'none' }}>
          Sign out
        </Button>
      </Box>
    );
  }
  return (
    <WhoAmIContext.Provider value={status.me}>{children}</WhoAmIContext.Provider>
  );
};

export default AdminGate;
